/* eslint-disable consistent-return */
import { lazy, useCallback, useEffect, useState } from "react";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
import SearchListDesktop from "../SearchList/SearchListDesktop";
import SearchHeadingContentDesktop from "../SearchHeadingContent/SectionHeadingContentDesktop";
import SearchMapWrapper from "../DesktopMapSection/SearchMapWrapper";
import styles from "../../Desktop/SearchDesktopV2.module.scss";
import searchListContainerStyles from "./SearchListContainer.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import SearchList from "../SearchList";
import ListViewIcon from "@Icons/ListViewIcon";
import GridViewIcon from "@Icons/GridViewIcon";
import wrapperHOC from "@Utils/wrapperHOC";
import USPSection from "@Pages/PropertyPage/components/USPSection";
import MapToggleButton from "../MapToggleButton/MapToggleButton";
import { useAmberSales } from "hooks/useAmberSales";
import { AppState } from "@Store/index";
import { AMBER_USER_WEBSITE_CONFIGURABLE_COMPONENTS } from "constants/index";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import AmberSalesBanner from "@Components/AmberSalesBanner";
import useShowGridView from "hooks/useShowGridView";
import setCookie from "helpers/setCookie";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import ApplicationConfigBanner from "../SearchHeadingContent/ApplicationConfigBanner";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import getCookie from "helpers/getCookie";
import useTranslationHook from "hooks/useTranslationHook";
import { MAP_VIEW_LIST_SCROLL_EVENT } from "../DesktopMapSection/clusteringMapUtils";

const LeadFormLayout = lazy(() => import("@Components/LeadForm/LeadFormLayout"));

const SearchListContainer = ({ handleOnMapOpen, showMap, isAgentView }: any) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const showGridViewFromStore = useShowGridView();
  const { isBelow } = useResponsiveness();
  const isBelowMd = isBelow(SCREEN_SIZE.MD);
  const nearbyParams = (() => {
    if (typeof window === "undefined") return undefined;
    const params = new URLSearchParams(window.location.search);
    const localityParam = params.get("locality");
    // Check if locality includes "near-by" to match the region name pattern
    if (!localityParam || !localityParam.includes("near-by")) return undefined;
    const lat = parseFloat(getCookie("nearby_lat") || "");
    const lng = parseFloat(getCookie("nearby_lng") || "");
    if (Number.isNaN(lat) || Number.isNaN(lng)) return undefined;
    return { lat, lng };
  })();
  const [showGridView, setShowGridView] = useState(showGridViewFromStore || Boolean(isBelowMd));
  const [mapScrollContainer, setMapScrollContainer] = useState<HTMLDivElement | null>(null);
  const { mainBannerImage, isSaleOfferAvailable }: any = useAmberSales({
    page: "SEARCH",
  });
  const { configComponents, regionId } = useSelector(
    ({ appData, searchPageData }: AppState) => ({
      configComponents: appData.configComponents,
      regionId: searchPageData?.region?.id,
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (isAgentView) {
      setShowGridView(false);
    }
    if (isBelowMd && !isAgentView) {
      setShowGridView(isBelowMd);
      if (showMap) {
        toggleMap();
      }
    }
  }, [isBelowMd, isAgentView]);

  useEffect(() => {
    if (!showMap || !mapScrollContainer) return undefined;

    const handleScroll = () => {
      window.dispatchEvent(new Event(MAP_VIEW_LIST_SCROLL_EVENT));
    };

    mapScrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      mapScrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [showMap, mapScrollContainer]);

  const searchPageTopBannerConfig = configComponents?.filter(
    (configComponent: any) =>
      configComponent?.component_name ===
        AMBER_USER_WEBSITE_CONFIGURABLE_COMPONENTS["search_page_top_banner"] &&
      configComponent?.regionId === regionId,
  )[0];

  const toggleMap = useCallback(() => {
    window.scrollTo(0, 0);
    gtagEventEmitter({
      category: "search_page",
      action: showMap ? "close_map_click" : "explore_map_click",
    });
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        handleOnMapOpen(!showMap);
        window.removeLazyMapLoad = true;
      });
    } else {
      handleOnMapOpen(!showMap);
    }
  }, [showMap]);

  const toggleGridView = useCallback(
    (isGridView = false) => {
      const persist = () => {
        setShowGridView(isGridView);
        gtagEventEmitter({
          category: "search_page",
          action: "view_toggle_click",
          value: isGridView ? "grid" : "list",
        });
        dispatch({ type: "appData/updateShowGridView", payload: isGridView });
        setCookie({
          cname: "search_grid_view",
          cvalue: isGridView ? "true" : "false",
          exdays: 365,
        });
      };
      if (document.startViewTransition) {
        document.startViewTransition(persist);
      } else {
        persist();
      }
    },
    [dispatch],
  );

  if (showMap) {
    return (
      <div className={styles.searchListMapContainer}>
        <div className={styles.searchContentSection}>
          <div ref={setMapScrollContainer} className={searchListContainerStyles.mapViewListScroll}>
            <div className={styles.headingWrapper}>
              <div className={styles.searchHeader}>
                <SearchHeadingContentDesktop hideBreadcrumb />
              </div>
            </div>
            <SearchList showGridView isMapView scrollContainer={mapScrollContainer} />
          </div>
        </div>
        <div className={styles.mapSection} style={{ viewTransitionName: "map-section" }}>
          <SearchMapWrapper
            showMapboxGlobal
            enableClustering
            userCoordinates={nearbyParams ?? undefined}
            onCloseMap={toggleMap}
            closeMapLabel={t("searchpage.components.searchlistcontainer.closemapbutton")}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={formatClassNames(
        styles.searchListFilterContainer,
        isAgentView && styles.agentView,
      )}
    >
      <div className={styles.headingWrapper}>
        <div className={styles.searchHeader}>
          <SearchHeadingContentDesktop />
          {!isAgentView && (
            <div className={searchListContainerStyles.viewToggleWrapper}>
              <div
                className={formatClassNames(
                  searchListContainerStyles.view,
                  !showGridView && searchListContainerStyles.active,
                )}
                role="none"
                onClick={() => toggleGridView(false)}
              >
                <ListViewIcon />
                {t("searchpage.components.searchlistcontainer.listviewtoggle")}
              </div>
              <div
                className={formatClassNames(
                  searchListContainerStyles.view,
                  showGridView && searchListContainerStyles.active,
                )}
                role="none"
                onClick={() => toggleGridView(true)}
              >
                <GridViewIcon />
                {t("searchpage.components.searchlistcontainer.gridviewtoggle")}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.searchFilterWrapper}>
        <div
          className={formatClassNames(styles.searchContentSection, showGridView && styles.gridView)}
        >
          <ApplicationConfigBanner />
          {isSaleOfferAvailable && !searchPageTopBannerConfig && (
            <div style={{ pointerEvents: "none" }}>
              <AmberSalesBanner
                src={mainBannerImage}
                id="search-fest-banner-desktop"
                className={styles.saleOfferBannerContainer}
                isEagerLoad
              />
            </div>
          )}
          {showGridView ? (
            <SearchList showGridView />
          ) : (
            <SearchListDesktop isAgentView={isAgentView} />
          )}
          <LazyLoadOnScroll fallback={<section style={{ height: "630px" }} />}>
            <LeadFormLayout />
          </LazyLoadOnScroll>
        </div>
        <div className={formatClassNames(styles.filterSection, isAgentView && styles.isAgentView)}>
          <div className={styles.item}>
            <MapToggleButton
              showMap={showMap}
              onClick={toggleMap}
              style={{ viewTransitionName: "map-section" }}
            />
          </div>
          <USPSection isDesktop />
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(SearchListContainer, {
  componentName: "SearchListContainer",
  showForChina: true,
});
